import React, { useContext } from 'react';
import { getContext } from 'common';

import List, { ListProps } from 'components/List';
import { SideDrawerContext } from './';

import Styled, { COMPACT_NAVIGATION_DRAWER_WIDTH } from './styles';

const ELEVATION_INDEX = 0;
const ELEVATION_TRANSPARENT = true;

const DEFAULT_LIST_STYLE = {
  minHeight: '100%',
  boxShadow: 'none',
  padding: 0,
};

const SideDrawerListContext = React.createContext<any>({});

export interface SideDrawerListProps extends React.ComponentPropsWithRef<'div'> {
  /** The variant of the SideDrawerList. 'slim' variant styles the submenu(s) more compactly. 'all-slim' variant styles the entire list more compactly. */
  variant?: 'slim' | 'all-slim' | 'default';
  /** To configure the internal List component. Refer to the List docs to learn more. */
  listProps?: Partial<ListProps>;
  /** The content of the list, consider using `SideDrawerListItem` and `SideDrawerListItemDivider` */
  children: React.ReactNode;
  /** To customize the wrapper style. */
  style?: React.CSSProperties
}

/** `SideDrawerList` is made to be used as child of `SideDrawer`. All other props than `SideDrawerList`'s props will be provided to the root element */
const SideDrawerList = React.forwardRef<HTMLElement, SideDrawerListProps>((props: SideDrawerListProps, ref) => getContext(({ theme }) => {
  const {
    style,
    children,
    variant,
    listProps,
    ...otherProps
  } = props;
  const sidedrawerContext = useContext(SideDrawerContext);
  const isCompactAndClosed = sidedrawerContext.variant === 'compact' && !sidedrawerContext.visible;
  const {
    style: configuredListStyle,
    ...otherListProps
  } = listProps;

  return (
    <Styled.SideDrawerList $theme={theme.LIST.VERTICAL} $isCompactAndClosed={isCompactAndClosed} {...otherProps}>
      <List
        style={{
          width: isCompactAndClosed ? COMPACT_NAVIGATION_DRAWER_WIDTH : undefined,
          maxWidth: isCompactAndClosed ? COMPACT_NAVIGATION_DRAWER_WIDTH : undefined,
          minWidth: isCompactAndClosed ? COMPACT_NAVIGATION_DRAWER_WIDTH : undefined,
          ...DEFAULT_LIST_STYLE,
          ...configuredListStyle
        }}
        ref={ref}
        elevationProps={{ elevationIndex: ELEVATION_INDEX, transparent: ELEVATION_TRANSPARENT }}
        truncateListText
        {...otherListProps}
      >
        <SideDrawerListContext.Provider value={{ variant }}>
          {children}
        </SideDrawerListContext.Provider>
      </List>
    </Styled.SideDrawerList>
  );
}));

SideDrawerList.displayName = 'SideDrawerList';

SideDrawerList.defaultProps = {
  variant: 'default',
  listProps: {},
  style: {}
};

export {
  SideDrawerList as default,
  SideDrawerListContext,
};
